import chunk from 'lodash/chunk';

import { projectsStardogRequestDispatchers } from 'src/actions/projects/actionCreators';
import { fetchModelClassesAndProperties } from 'src/actions/projects/utils/fetchModelClassesAndProperties';
import { PROJECT_MODEL_LABEL_LIMIT } from 'src/constants/limits';
import { Connection } from 'src/store/connections/types';
import { parseMetadataResults } from 'src/utils/jsonld/parseMetadataResults';
import { buildProjectModelRules } from 'src/utils/project/buildProjectModelRules';

/**
 * Fetches and builds model metadata by querying for classes, properties, labels,
 * descriptions, geo data, and rules.
 * Returns the processed metadata without dispatching any redux actions.
 */
export const fetchModelMetadata = async ({
  connection,
  databaseId,
  databaseNamespaces,
  modelId,
  modelName,
  namedGraphs,
  projectId,
}: {
  connection: Connection;
  databaseId: string;
  databaseNamespaces: Record<string, string>;
  modelId: string;
  modelName: string;
  namedGraphs: string[];
  projectId: string;
}) => {
  // Fetch classes and properties first
  const { classes, properties } = await fetchModelClassesAndProperties({
    projectId,
    modelName,
    databaseId,
    namedGraphs,
    shouldNotDispatch: true,
  });

  // Collect all IRIs that need labels/descriptions
  const allIris = new Set<string>();
  const classIris = new Set<string>();

  Object.values(classes || {}).forEach(({ iri, parents }) => {
    allIris.add(iri);
    classIris.add(iri);
    parents.forEach((parent) => {
      allIris.add(parent);
      classIris.add(parent);
    });
  });

  Object.values(properties || {}).forEach(
    ({ iri, domain: domains, range: ranges, parents, isDatatype }) => {
      allIris.add(iri);
      domains.forEach((domain) => {
        allIris.add(domain);
        classIris.add(domain);
      });
      ranges.forEach((range) => {
        allIris.add(range);
        if (!isDatatype) {
          classIris.add(range);
        }
      });
      parents.forEach((parent) => allIris.add(parent));
    }
  );

  // Fetch labels and descriptions (chunked)
  const labelChunks = chunk(Array.from(allIris), PROJECT_MODEL_LABEL_LIMIT);
  const labelResponses = await Promise.all(
    labelChunks.map((iris) =>
      projectsStardogRequestDispatchers.requestProjectModelLabelsAndDescriptions(
        {
          id: projectId,
          modelId,
          connection,
          databaseId,
          namedGraphs,
          iris,
          shouldNotDispatch: true,
        }
      )
    )
  );

  // Fetch geo data (chunked)
  const geoChunks = chunk(Array.from(classIris), PROJECT_MODEL_LABEL_LIMIT);
  const geoResponses = await Promise.all(
    geoChunks.map((iris) =>
      projectsStardogRequestDispatchers.requestProjectModelGeoData({
        id: projectId,
        modelId,
        connection,
        databaseId,
        namedGraphs,
        iris,
        shouldNotDispatch: true,
      })
    )
  );

  // Parse label/description responses
  const allLabelResults = labelResponses.flatMap(
    (r) => r.payload.response?.body || []
  );
  const labelsAndDescriptions = parseMetadataResults(allLabelResults);
  const { colorIdsByIri, descriptionsByIri, labelsByIri } =
    labelsAndDescriptions;

  // Parse geo data responses
  const allGeoResults = geoResponses.flatMap(
    (r) => r.payload.response?.body || []
  );
  const { geoDataByIri: parsedGeoData } = parseMetadataResults(allGeoResults);

  // Merge geo data from labels query with dedicated geo data query
  const geoDataByIri = {
    ...labelsAndDescriptions.geoDataByIri,
    ...parsedGeoData,
  };

  // Fetch rules
  const rulesResponse =
    await projectsStardogRequestDispatchers.requestProjectModelRules({
      id: projectId,
      modelId,
      databaseId,
      namedGraphs,
      connection,
      shouldNotDispatch: true,
    });

  const rulesBindings =
    rulesResponse.payload.response?.body?.results?.bindings || [];

  // Build rules using shared utility
  const rules = buildProjectModelRules({
    bindings: rulesBindings,
    classes,
    colorIdsByIri,
    databaseNamespaces,
    labelsByIri,
    modelName,
    properties,
  });

  return {
    classes,
    properties,
    colorIdsByIri,
    descriptionsByIri,
    geoDataByIri,
    labelsByIri,
    rules,
  };
};
